import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { KeyRound, Loader2, Search, LockOpen, Lock, Copy, Check, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const CLASSES = ["Play Group", "LKG", "UKG", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"];
const SECTIONS = ["A", "B", "C", "D", "E"];

const relTime = (iso) => {
  if (!iso) return "Never signed in";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString();
  } catch { return "—"; }
};

export default function DevAdminStudentPasswords({ embedded = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [cls, setCls] = useState("all");
  const [sec, setSec] = useState("all");
  const [lockedOnly, setLockedOnly] = useState(false);
  const [resetting, setResetting] = useState(null);
  const [pwResult, setPwResult] = useState(null); // { name, password }
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkResult, setBulkResult] = useState(null); // { count, reset: [] }

  const lockedCount = students.filter((s) => s.locked).length;
  const visible = lockedOnly ? students.filter((s) => s.locked) : students;

  const doBulkReset = async () => {
    setBulkRunning(true);
    try {
      const params = {};
      if (cls !== "all") params.class_name = cls;
      if (sec !== "all") params.section = sec;
      const { data } = await api.post("/devadmin/students/bulk-reset-locked", null, { params });
      setBulkOpen(false);
      setBulkResult(data);
      toast.success(`Reset ${data.count} locked student${data.count === 1 ? "" : "s"}`);
      load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Bulk reset failed");
    } finally { setBulkRunning(false); }
  };

  useEffect(() => {
    if (user && !user.is_dev_admin) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (cls !== "all") params.class_name = cls;
      if (sec !== "all") params.section = sec;
      const { data } = await api.get("/devadmin/student-passwords", { params });
      setStudents(data.students || []);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Failed to load students");
    } finally { setLoading(false); }
  }, [q, cls, sec]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const doReset = async () => {
    const s = resetting;
    setResetting(null);
    try {
      const { data } = await api.post(`/devadmin/students/${s.id}/reset-password`);
      setPwResult({ name: s.name, password: data.password });
      setCopied(false);
      load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not reset password");
    }
  };

  const doUnlock = async (s) => {
    setBusy(s.id);
    try {
      await api.post(`/devadmin/students/${s.id}/unlock`);
      toast.success(`${s.name} unlocked`);
      load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not unlock");
    } finally { setBusy(null); }
  };

  const inner = (
    <>
      <div className={embedded ? "" : "max-w-5xl mx-auto"} data-testid="devadmin-student-passwords-page">
        {!embedded && (
          <div className="flex items-center gap-3 mb-2">
            <div className="h-10 w-10 rounded-xl bg-fuchsia-600 text-white flex items-center justify-center">
              <KeyRound className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>Student Passwords & Lockouts</h1>
              <p className="text-sm text-slate-500">Reset a student's password to their Student ID, or unlock an account after too many wrong attempts.</p>
            </div>
          </div>
        )}

        <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 text-xs px-3 py-2 mb-4">
          Passwords are stored encrypted and cannot be displayed. "Reset" sets the password to the student's Student ID and shows it once so you can share it.
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-end gap-3 mb-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, roll or Student ID" className="h-10 rounded-xl pl-9" data-testid="sp-search" />
          </div>
          <Select value={cls} onValueChange={setCls}>
            <SelectTrigger className="h-10 rounded-xl w-[130px]" data-testid="sp-class"><SelectValue placeholder="Class" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All classes</SelectItem>{CLASSES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={sec} onValueChange={setSec}>
            <SelectTrigger className="h-10 rounded-xl w-[120px]" data-testid="sp-section"><SelectValue placeholder="Section" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All sections</SelectItem>{SECTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Button
            variant={lockedOnly ? "default" : "outline"}
            className={"h-10 rounded-xl " + (lockedOnly ? "bg-rose-500 hover:bg-rose-600 text-white" : "")}
            onClick={() => setLockedOnly((v) => !v)}
            data-testid="sp-locked-filter"
            aria-pressed={lockedOnly}
          >
            <Lock className="h-4 w-4 mr-1.5" /> {lockedOnly ? "Showing locked" : "Locked only"} ({lockedCount})
          </Button>
          <Button
            variant="outline"
            className="h-10 rounded-xl border-rose-200 text-rose-600 hover:bg-rose-50 disabled:opacity-40 dark:border-rose-800 dark:hover:bg-rose-900/20"
            disabled={lockedCount === 0}
            onClick={() => setBulkOpen(true)}
            data-testid="sp-bulk-reset-btn"
          >
            <LockOpen className="h-4 w-4 mr-1.5" /> Reset all locked ({lockedCount})
          </Button>
        </div>

        {loading ? (
          <div className="py-16 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading…</div>
        ) : visible.length === 0 ? (
          <div className="text-sm text-slate-400 py-10 text-center" data-testid="sp-empty">{lockedOnly ? "No locked students right now. 🎉" : "No students match."}</div>
        ) : (
          <div className="space-y-2" data-testid="sp-list">
            {visible.map((s) => (
              <div key={s.id} className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 flex items-center justify-between gap-4" data-testid={`sp-row-${s.roll_number}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-slate-900 dark:text-slate-100 truncate">{s.name}</span>
                    <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-500">{s.class_name}-{s.section} · #{s.roll_number}</span>
                    {s.locked && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300 inline-flex items-center gap-1" data-testid={`sp-locked-${s.roll_number}`}><Lock className="h-2.5 w-2.5" /> Locked</span>}
                    {s.must_change_password && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300">Pending change</span>}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">Student ID: <code className="font-mono">{s.student_id}</code> · Last login: {relTime(s.last_login_at)}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {s.locked && (
                    <Button variant="outline" size="sm" className="rounded-xl" onClick={() => doUnlock(s)} disabled={busy === s.id} data-testid={`sp-unlock-${s.roll_number}`}>
                      {busy === s.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><LockOpen className="h-3.5 w-3.5 mr-1" /> Unlock</>}
                    </Button>
                  )}
                  <Button size="sm" className="rounded-xl bg-rose-500 hover:bg-rose-600" onClick={() => setResetting(s)} data-testid={`sp-reset-${s.roll_number}`}>
                    <KeyRound className="h-3.5 w-3.5 mr-1" /> Reset password
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reset confirm */}
      <Dialog open={!!resetting} onOpenChange={(v) => !v && setResetting(null)}>
        <DialogContent className="sm:max-w-md rounded-2xl" data-testid="sp-reset-confirm">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit" }}><ShieldAlert className="inline h-5 w-5 mr-2 -mt-1 text-rose-500" />Reset password?</DialogTitle>
            <DialogDescription>
              This sets <strong>{resetting?.name}</strong>'s password to their Student ID (<code className="font-mono">{resetting?.student_id}</code>), unlocks the account, and forces a change on next login.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetting(null)}>Cancel</Button>
            <Button className="bg-rose-500 hover:bg-rose-600" onClick={doReset} data-testid="sp-reset-confirm-btn">Reset password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Result */}
      <Dialog open={!!pwResult} onOpenChange={(v) => !v && setPwResult(null)}>
        <DialogContent className="sm:max-w-md rounded-2xl" data-testid="sp-password-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit" }}><KeyRound className="inline h-5 w-5 mr-2 -mt-1 text-sky-500" />Password reset</DialogTitle>
            <DialogDescription>Share this with {pwResult?.name}. They must change it on next sign-in.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-3">
            <code className="flex-1 text-lg font-mono text-slate-900 dark:text-slate-100" data-testid="sp-password-value">{pwResult?.password}</code>
            <Button size="sm" variant="outline" className="rounded-lg" onClick={() => {
              navigator.clipboard?.writeText(pwResult?.password || "");
              setCopied(true); toast.success("Copied");
            }} data-testid="sp-copy-password">
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
          <DialogFooter>
            <Button onClick={() => setPwResult(null)} data-testid="sp-password-done">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk reset confirm */}
      <Dialog open={bulkOpen} onOpenChange={(v) => !v && setBulkOpen(false)}>
        <DialogContent className="sm:max-w-md rounded-2xl" data-testid="sp-bulk-confirm">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit" }}><LockOpen className="inline h-5 w-5 mr-2 -mt-1 text-rose-500" />Reset all locked students?</DialogTitle>
            <DialogDescription>
              This resets <strong>{lockedCount}</strong> currently-locked student{lockedCount === 1 ? "" : "s"}
              {cls !== "all" ? ` in class ${cls}${sec !== "all" ? `-${sec}` : ""}` : ""} to their Student ID and clears their lockout. Each must change it on next sign-in.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
            <Button className="bg-rose-500 hover:bg-rose-600" onClick={doBulkReset} disabled={bulkRunning} data-testid="sp-bulk-confirm-btn">
              {bulkRunning ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Resetting…</> : `Reset ${lockedCount}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk result */}
      <Dialog open={!!bulkResult} onOpenChange={(v) => !v && setBulkResult(null)}>
        <DialogContent className="sm:max-w-md rounded-2xl" data-testid="sp-bulk-result">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit" }}><Check className="inline h-5 w-5 mr-2 -mt-1 text-emerald-500" />{bulkResult?.count || 0} students reset</DialogTitle>
            <DialogDescription>Each student's new password is their Student ID (shown below). They must change it on next sign-in.</DialogDescription>
          </DialogHeader>
          <div className="max-h-64 overflow-y-auto space-y-1.5">
            {(bulkResult?.reset || []).map((r) => (
              <div key={r.student_id} className="flex items-center justify-between text-sm rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2">
                <span className="truncate">{r.name} <span className="text-slate-400">#{r.roll_number}</span></span>
                <code className="font-mono text-slate-900 dark:text-slate-100">{r.student_id}</code>
              </div>
            ))}
            {(!bulkResult?.reset || bulkResult.reset.length === 0) && <p className="text-sm text-slate-400 text-center py-3">No locked students were found.</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              const text = (bulkResult?.reset || []).map((r) => `${r.name} (#${r.roll_number}): ${r.student_id}`).join("\n");
              navigator.clipboard?.writeText(text); toast.success("Copied list");
            }} data-testid="sp-bulk-copy"><Copy className="h-4 w-4 mr-1" /> Copy list</Button>
            <Button onClick={() => setBulkResult(null)} data-testid="sp-bulk-done">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

  if (embedded) return inner;
  return <Layout>{inner}</Layout>;
}
